import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Layers,
  Radio,
  Building2,
  Activity,
  Battery,
} from 'lucide-react';

import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { DetectionTrendChart } from '@/components/dashboard/DetectionTrendChart';
import { ScannerStatusChart } from '@/components/dashboard/ScannerStatusChart';
import { ItemsByBranchChart } from '@/components/dashboard/ItemsByBranchChart';
import { ActivityTimelineChart } from '@/components/dashboard/ActivityTimelineChart';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { QuickActions } from '@/components/dashboard/QuickActions';
import type { Item, Shelf, Scanner, Branch, Detection } from '@packages/shared-types';
import { listItems } from '@/services/itemService';
import { listShelves } from '@/services/shelfService';
import { listScanners } from '@/services/scannerService';
import { listBranches } from '@/services/branchService';
import { listDetections } from '@/services/detectionService';

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const title = useIndustryTranslation('pages.dashboard.title');
  const description = useIndustryTranslation('pages.dashboard.description');
  const itemsTracked = useIndustryTranslation('pages.dashboard.stats.itemsTracked');
  const itemsTrackedHint = useIndustryTranslation('pages.dashboard.stats.itemsTrackedHint');
  const shelvesActive = useIndustryTranslation('pages.dashboard.stats.shelvesActive');
  const shelvesActiveHint = useIndustryTranslation('pages.dashboard.stats.shelvesActiveHint');
  const scannersOnline = useIndustryTranslation('pages.dashboard.stats.scannersOnline');
  const scannersOnlineHint = useIndustryTranslation('pages.dashboard.stats.scannersOnlineHint');
  const branchesLabel = useIndustryTranslation('pages.dashboard.stats.branches');
  const branchesHint = useIndustryTranslation('pages.dashboard.stats.branchesHint');
  const recentDetectionsLabel = useIndustryTranslation('pages.dashboard.stats.recentDetections');
  const recentDetectionsHint = useIndustryTranslation('pages.dashboard.stats.recentDetectionsHint');
  const lowBatteryScannersLabel = useIndustryTranslation('pages.dashboard.stats.lowBatteryScanners');
  const lowBatteryScannersHint = useIndustryTranslation('pages.dashboard.stats.lowBatteryScannersHint');
  const itemsByBranchChartLabel = useIndustryTranslation('pages.dashboard.charts.itemsByBranch');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [, setShelves] = useState<Shelf[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [activeShelvesCount, setActiveShelvesCount] = useState(0);
  const [onlineScannersCount, setOnlineScannersCount] = useState(0);
  const [branchesCount, setBranchesCount] = useState(0);
  const [recentDetectionsCount, setRecentDetectionsCount] = useState(0);
  const [lowBatteryScannersCount, setLowBatteryScannersCount] = useState(0);

  const formatNumber = useCallback((num: number): string => {
    return new Intl.NumberFormat().format(num);
  }, []);

  const loadDashboardData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const [items, shelves, scanners, branches, allDetections] = await Promise.all([
        listItems(),
        listShelves(),
        listScanners(),
        listBranches(),
        listDetections(),
      ]);

      // Store raw data for charts
      setItems(items);
      setShelves(shelves);
      setScanners(scanners);
      setBranches(branches);
      setDetections(allDetections);

      // Filter detections from last 24 hours
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const recentDetections = allDetections.filter(
        (detection) => new Date(detection.detectedAt) > twentyFourHoursAgo,
      );

      // Calculate metrics
      setItemsCount(items.length);

      // Active shelves: shelves that have a scanner assigned
      const activeShelves = shelves.filter((shelf) => shelf.scannerId !== null);
      setActiveShelvesCount(activeShelves.length);

      // Online scanners: active status AND checked in within last 24 hours
      const onlineScanners = scanners.filter(
        (scanner) =>
          scanner.status === 'active' &&
          scanner.lastCheckinAt &&
          new Date(scanner.lastCheckinAt) > twentyFourHoursAgo,
      );
      setOnlineScannersCount(onlineScanners.length);

      // Branches count
      setBranchesCount(branches.length);

      // Recent detections (last 24 hours)
      setRecentDetectionsCount(recentDetections.length);

      // Low battery scanners: battery < 20% and not charging
      const lowBatteryScanners = scanners.filter(
        (scanner) =>
          scanner.batteryLevel != null &&
          scanner.batteryLevel < 20 &&
          scanner.status !== 'charging',
      );
      setLowBatteryScannersCount(lowBatteryScanners.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
      console.error('Failed to load dashboard data', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboardData();
  }, [loadDashboardData]);

  const statCards = useMemo(
    () => [
      {
        label: itemsTracked,
        value: formatNumber(itemsCount),
        hint: itemsTrackedHint,
        icon: Package,
        gradient: 'from-blue-500/10 to-blue-600/5 dark:from-blue-500/20 dark:to-blue-600/10',
        borderColor: 'border-blue-500/30 dark:border-l-blue-500/50',
        textColor: 'text-blue-600 dark:text-blue-500',
        iconColor: 'text-blue-500',
      },
      {
        label: shelvesActive,
        value: formatNumber(activeShelvesCount),
        hint: shelvesActiveHint,
        icon: Layers,
        gradient: 'from-green-500/10 to-green-600/5 dark:from-green-500/20 dark:to-green-600/10',
        borderColor: 'border-green-500/30 dark:border-l-green-500/50',
        textColor: 'text-green-600 dark:text-green-500',
        iconColor: 'text-green-500',
      },
      {
        label: scannersOnline,
        value: formatNumber(onlineScannersCount),
        hint: scannersOnlineHint,
        icon: Radio,
        gradient: 'from-purple-500/10 to-purple-600/5 dark:from-purple-500/20 dark:to-purple-600/10',
        borderColor: 'border-purple-500/30 dark:border-l-purple-500/50',
        textColor: 'text-purple-600 dark:text-purple-500',
        iconColor: 'text-purple-500',
      },
      {
        label: branchesLabel,
        value: formatNumber(branchesCount),
        hint: branchesHint,
        icon: Building2,
        gradient: 'from-teal-500/10 to-teal-600/5 dark:from-teal-500/20 dark:to-teal-600/10',
        borderColor: 'border-teal-500/30 dark:border-l-teal-500/50',
        textColor: 'text-teal-600 dark:text-teal-500',
        iconColor: 'text-teal-500',
      },
      {
        label: recentDetectionsLabel,
        value: formatNumber(recentDetectionsCount),
        hint: recentDetectionsHint,
        icon: Activity,
        gradient: 'from-orange-500/10 to-orange-600/5 dark:from-orange-500/20 dark:to-orange-600/10',
        borderColor: 'border-orange-500/30 dark:border-l-orange-500/50',
        textColor: 'text-orange-600 dark:text-orange-500',
        iconColor: 'text-orange-500',
      },
      {
        label: lowBatteryScannersLabel,
        value: formatNumber(lowBatteryScannersCount),
        hint: lowBatteryScannersHint,
        icon: Battery,
        gradient: 'from-yellow-500/10 to-red-600/5 dark:from-yellow-500/20 dark:to-yellow-600/10',
        borderColor: 'border-yellow-500/30 dark:border-l-yellow-500/50',
        textColor: 'text-yellow-600 dark:text-yellow-500',
        iconColor: 'text-yellow-500',
        variant: lowBatteryScannersCount > 0 ? 'warning' : 'default',
      },
    ],
    [
      itemsTracked,
      itemsTrackedHint,
      shelvesActive,
      shelvesActiveHint,
      scannersOnline,
      scannersOnlineHint,
      branchesLabel,
      branchesHint,
      recentDetectionsLabel,
      recentDetectionsHint,
      lowBatteryScannersLabel,
      lowBatteryScannersHint,
      itemsCount,
      activeShelvesCount,
      onlineScannersCount,
      branchesCount,
      recentDetectionsCount,
      lowBatteryScannersCount,
      formatNumber,
    ],
  );

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={title} description={description} />
      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="bg-card/90 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-8 w-16 animate-pulse rounded bg-muted" />
                  <div className="mt-2 h-3 w-32 animate-pulse rounded bg-muted" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className={`bg-gradient-to-br ${stat.gradient} border-l-4 ${stat.borderColor} shadow-sm hover:shadow-md transition-shadow`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.label}
                    </CardTitle>
                    <Icon className={`h-5 w-5 ${stat.iconColor}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-semibold ${stat.textColor}`}>
                    {stat.value}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{stat.hint}</p>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('pages.dashboard.charts.detectionTrend')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DetectionTrendChart detections={detections} isLoading={isLoading} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('pages.dashboard.charts.scannerStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ScannerStatusChart scanners={scanners} isLoading={isLoading} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{itemsByBranchChartLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <ItemsByBranchChart
              items={items}
              branches={branches}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('pages.dashboard.charts.activityTimeline')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityTimelineChart detections={detections} isLoading={isLoading} />
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed and Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2">
        <ActivityFeed detections={detections} isLoading={isLoading} />
        <QuickActions />
      </div>
    </div>
  );
}
